/**
 * Estado do assistente de Impulsionar e a conversão para o pedido do
 * servidor. Puro: sem React, sem tRPC — testável.
 */

import {
  OBJETIVOS_IMPULSIONAR,
  PUBLICO_PADRAO,
  nomeDoImpulsionamento,
  publicoValido,
  type ObjetivoImpulsionar,
  type PublicoForm,
} from "@shared/meta-ads-boost";
import { reaisParaCentavos } from "./meta-ads-format";

export type MediaEscolhida = {
  id: string;
  caption: string | null;
  permalink: string | null;
  thumbUrl: string | null;
  mediaProductType: string | null;
};

export type BoostForm = {
  conteudoTipo: "instagram_post" | "link";
  media: MediaEscolhida | null;
  linkAd: { message: string; link: string; imageUrl: string; imageHash: string | null; headline: string };
  objetivo: ObjetivoImpulsionar | null;
  /** Destino do objetivo "trafego". */
  link: string;
  publico: PublicoForm;
  verba: { dailyTexto: string; inicio: "now" | "date"; dataInicio: string; horaInicio: string; dataFim: string };
  nome: string;
  nomeEditado: boolean;
};

export type StepKey = "conteudo" | "objetivo" | "publico" | "verba" | "revisao";
export const STEP_KEYS: StepKey[] = ["conteudo", "objetivo", "publico", "verba", "revisao"];

export const FORM_INICIAL: BoostForm = {
  conteudoTipo: "instagram_post",
  media: null,
  linkAd: { message: "", link: "", imageUrl: "", imageHash: null, headline: "" },
  objetivo: null,
  link: "",
  publico: PUBLICO_PADRAO,
  verba: { dailyTexto: "50,00", inicio: "now", dataInicio: "", horaInicio: "09:00", dataFim: "" },
  nome: "",
  nomeEditado: false,
};

export type ContextoValidacao = {
  capCents: number | null;
  minCents: number;
  instagramConectado: boolean;
  whatsappOk: boolean | null;
  agora?: Date;
};

const urlValida = (u: string) => /^https?:\/\/\S+$/i.test(u.trim());

function inicioDate(f: BoostForm, agora: Date): Date | null {
  if (f.verba.inicio === "now") return agora;
  if (!f.verba.dataInicio) return null;
  const d = new Date(`${f.verba.dataInicio}T${f.verba.horaInicio || "09:00"}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fimDate(f: BoostForm): Date | null {
  if (!f.verba.dataFim) return null;
  const d = new Date(`${f.verba.dataFim}T23:59:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Mensagem do que falta na etapa, ou null se completa. */
export function pendenciaDaEtapa(step: StepKey, f: BoostForm, ctx: ContextoValidacao): string | null {
  const agora = ctx.agora ?? new Date();
  switch (step) {
    case "conteudo":
      if (f.conteudoTipo === "instagram_post") {
        if (!ctx.instagramConectado) return "Conecte o Instagram ou crie um anúncio de link.";
        return f.media ? null : "Escolha um post ou reel.";
      }
      if (!f.linkAd.message.trim()) return "Escreva o texto do anúncio.";
      if (!urlValida(f.linkAd.link)) return "Informe o link de destino.";
      if (!f.linkAd.imageHash) return "Envie a imagem do anúncio.";
      return null;
    case "objetivo":
      if (!f.objetivo) return "Escolha o objetivo.";
      if (OBJETIVOS_IMPULSIONAR[f.objetivo].precisaLink && !urlValida(f.link) && !(f.conteudoTipo === "link" && urlValida(f.linkAd.link))) {
        return "Informe o link para onde o anúncio leva.";
      }
      if (f.objetivo === "whatsapp" && ctx.whatsappOk === false) return "A Página não tem WhatsApp vinculado.";
      return null;
    case "publico":
      return publicoValido(f.publico);
    case "verba": {
      const cents = reaisParaCentavos(f.verba.dailyTexto);
      if (cents == null) return "Informe a verba diária.";
      if (cents < ctx.minCents) return "Verba abaixo do mínimo.";
      if (ctx.capCents != null && cents > ctx.capCents) return "Verba acima do limite do workspace.";
      const ini = inicioDate(f, agora);
      if (!ini) return "Informe a data de início.";
      if (f.verba.inicio === "date" && ini.getTime() < agora.getTime() - 60_000) return "A data de início já passou.";
      const fim = fimDate(f);
      if (fim && fim.getTime() <= ini.getTime()) return "O fim precisa ser depois do início.";
      return null;
    }
    case "revisao":
      for (const s of ["conteudo", "objetivo", "publico", "verba"] as StepKey[]) {
        const p = pendenciaDaEtapa(s, f, ctx);
        if (p) return p;
      }
      return f.nome.trim() ? null : "Dê um nome ao impulsionamento.";
  }
}

export function isStepComplete(step: StepKey, f: BoostForm, ctx: ContextoValidacao): boolean {
  return pendenciaDaEtapa(step, f, ctx) === null;
}

/** Nome automático a partir do conteúdo, enquanto a pessoa não editou. */
export function nomeAutomatico(f: BoostForm, agora = new Date()): string {
  const legenda = f.conteudoTipo === "instagram_post" ? f.media?.caption ?? null : f.linkAd.message || f.linkAd.headline || null;
  return nomeDoImpulsionamento(legenda, agora);
}

export function resumoDaEtapa(step: StepKey, f: BoostForm): string {
  switch (step) {
    case "conteudo":
      if (f.conteudoTipo === "instagram_post") return f.media ? `Post: ${(f.media.caption ?? "sem legenda").slice(0, 50)}` : "Nenhum post escolhido";
      return f.linkAd.message ? `Anúncio de link: ${f.linkAd.message.slice(0, 50)}` : "Anúncio de link";
    case "objetivo":
      return f.objetivo ? OBJETIVOS_IMPULSIONAR[f.objetivo].label : "Não escolhido";
    case "publico": {
      const p = f.publico;
      const locais = p.locais.map((l) => l.name).slice(0, 3).join(", ") + (p.locais.length > 3 ? ` +${p.locais.length - 3}` : "");
      const genero = p.genero === "all" ? "todos" : p.genero === "male" ? "homens" : "mulheres";
      return `${locais || "sem lugar"} · ${p.idadeMin}–${p.idadeMax === 65 ? "65+" : p.idadeMax} · ${genero}${p.interesses.length ? ` · ${p.interesses.length} interesse(s)` : ""}${p.advantagePlus ? " · Advantage+" : ""}`;
    }
    case "verba":
      return `${f.verba.dailyTexto ? `R$ ${f.verba.dailyTexto}/dia` : "sem verba"} · ${f.verba.inicio === "now" ? "começa agora" : `começa ${f.verba.dataInicio || "?"}`}${f.verba.dataFim ? ` · até ${f.verba.dataFim}` : ""}`;
    case "revisao":
      return f.nome || "";
  }
}

export function formParaInput(f: BoostForm, ctx: { pageId: string; igUserId: string | null; adAccountId?: string; agora?: Date }) {
  const agora = ctx.agora ?? new Date();
  const cents = reaisParaCentavos(f.verba.dailyTexto) ?? 0;
  const ini = f.verba.inicio === "date" ? inicioDate(f, agora) : null;
  const fim = fimDate(f);
  const link = f.link.trim() || (f.conteudoTipo === "link" ? f.linkAd.link.trim() : "");
  return {
    objectiveKey: f.objetivo!,
    conteudo:
      f.conteudoTipo === "instagram_post"
        ? { tipo: "instagram_post" as const, igMediaId: f.media!.id, permalink: f.media!.permalink, thumbUrl: f.media!.thumbUrl, legenda: f.media!.caption }
        : { tipo: "link" as const, message: f.linkAd.message.trim(), link: f.linkAd.link.trim(), imageHash: f.linkAd.imageHash!, imageUrl: f.linkAd.imageUrl || null, headline: f.linkAd.headline.trim() || null },
    pageId: ctx.pageId,
    igUserId: ctx.igUserId,
    link: link || null,
    publico: f.publico,
    dailyBudgetCents: cents,
    startTime: ini ? ini.toISOString() : null,
    endTime: fim ? fim.toISOString() : null,
    name: f.nome.trim(),
    adAccountId: ctx.adAccountId,
  };
}
